import React, { useEffect, useRef, useState } from 'react';
import {
  Link,
  Redirect,
  Route,
  Switch,
  useHistory,
  useParams
} from 'react-router-dom';
import styled from 'styled-components';

import { CircularProgress, Tab, Tabs } from '@material-ui/core';

import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import PageTemplateNav from '../../common/page-template/PageTemplateNav';
import StatusLabel from '../../common/StatusLabel';
import WorkflowApiLevelLabel from './components/WorkflowApiLevelLabel';
import WorkflowMigrateButton from './components/WorkflowMigrateButton';
import WorkflowStatusActions from './components/WorkflowStatusActions';
import WorkflowTabEdit from './tabs/WorkflowTabEdit';
import WorkflowTabInfo from './tabs/WorkflowTabInfo';
import { useWorkflowContext } from './Workflow.context';
import {
  getStatusLabel,
  useFindWorkflow,
  useUpdateWorkflowConfig
} from './Workflow.hooks';
import { BaseWorkflowTabProps, WorkflowDto } from './Workflow.interface';

const StyledPageTemplate = styled(PageTemplate)`
  .page-template-dashboard {
    padding: 0;
  }
  .padded-content {
    padding: 20px 30px 0 30px;
  }
`;

const StyledTitle = styled.div`
  display: flex;
  flex-direction: column;
  .status-container {
    display: flex;
    margin-top: 5px;
    .status-label {
      margin-right: 8px;
    }
  }
`;

const getTapValue = (path: string) => {
  const array = path.split('/');
  return array[array.length - 1];
};

const WorkflowDetailPage: React.FC = () => {
  const history = useHistory();
  const { workflowId } = useParams<{ workflowId?: string }>();
  const { loadWorkflow } = useWorkflowContext();
  const tabValue = getTapValue(history.location.pathname);
  const configRef = useRef<WorkflowDto['config'] | undefined>();

  const {
    workflow: loadedWorkflow,
    isLoading,
    loadingError,
    findWorkflow
  } = useFindWorkflow(Number(workflowId || 0));
  const { updatedWorkflow, updateError, updateWorkflowConfig } =
    useUpdateWorkflowConfig(loadedWorkflow?.id || 0);
  const error: FetchError | undefined = loadingError || updateError;

  const [workflow, setWorkflow] = useState<WorkflowDto | undefined>();
  const [config, setConfig] = useState<WorkflowDto['config'] | undefined>();
  const [didUpdate, setDidUpdate] = useState<boolean>(false);

  const statusLabel = getStatusLabel(workflow?.status || 'draft');

  const tabProps: BaseWorkflowTabProps = {
    workflow: workflow!,
    onChange: (newWorkflow: Partial<WorkflowDto>) => {
      workflow && setWorkflow({ ...workflow, ...newWorkflow });
    }
  };

  useEffect(() => {
    workflowId && findWorkflow();
  }, [findWorkflow, workflowId]);

  useEffect(() => {
    loadedWorkflow && setWorkflow(loadedWorkflow);
  }, [loadedWorkflow]);

  useEffect(() => {
    if (updatedWorkflow) {
      const workflow: WorkflowDto = { ...updatedWorkflow };
      if (configRef.current) {
        workflow.config = configRef.current;
        setConfig(configRef.current);
        configRef.current = undefined;
      }
      tabProps?.onChange?.(workflow);
      setDidUpdate(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedWorkflow]);

  useEffect(() => {
    configRef.current = undefined;
    setConfig(configRef.current);
  }, [updateError]);

  if (!['info', 'edit'].includes(tabValue)) {
    // if navigating to /portal/admin/workflow/:workflowId redirect to edit tab
    return <Redirect to={`/portal/admin/workflow/${workflowId}/info`} />;
  }

  const StatusContainer = () => {
    if (!workflow) {
      return null;
    }
    return (
      <div className='status-container'>
        {workflow.version > 1 && (
          <StatusLabel
            status={`Published v${workflow.version - 1}`}
            color='published'
          />
        )}
        {workflow.status !== 'published' && (
          <StatusLabel
            status={`${statusLabel} v${workflow.version}`}
            color={(workflow.status as any) || 'draft'}
          />
        )}
        <WorkflowApiLevelLabel apiLevel={1} />
      </div>
    );
  };

  return (
    <StyledPageTemplate>
      <PageTemplateHeader>
        {isLoading && <CircularProgress size={20} />}
        <StyledTitle>
          <h2>{workflow?.name}</h2>
          {workflow && <StatusContainer />}
        </StyledTitle>
        <PageTemplateHeaderActions flex>
          {workflow && (
            <>
              <WorkflowMigrateButton
                workflow={workflow}
                onImport={(config: WorkflowDto['config']) => {
                  // only set config state after successful update
                  configRef.current = config;
                  updateWorkflowConfig(config);
                }}
              />
              <WorkflowStatusActions
                {...tabProps}
                onChange={(workflow: WorkflowDto) => {
                  // reload updated workflow in context
                  loadWorkflow(workflow.taskTypeName || workflow.id);
                  tabProps.onChange?.(workflow);
                }}
              />
            </>
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      <PageTemplateNav>
        <Tabs
          variant='scrollable'
          indicatorColor='primary'
          scrollButtons='auto'
          value={getTapValue(history.location.pathname)}
        >
          <Tab
            component={Link}
            label='Workflow Info'
            value='info'
            to='./info'
          />
          <Tab
            component={Link}
            label='Edit Workflow'
            value='edit'
            to='./edit'
          />
        </Tabs>
      </PageTemplateNav>
      <PageTemplateDashboard>
        <Switch>
          <Route
            exact
            path={[
              '/portal/admin/workflow/:workflowId/info',
              '/portal/jira/workflows/:workflowId/info'
            ]}
          >
            {workflow && (
              <div className='padded-content'>
                <WorkflowTabInfo {...tabProps} />
              </div>
            )}
          </Route>
          <Route
            exact
            path={[
              '/portal/admin/workflow/:workflowId/edit',
              '/portal/jira/workflows/:workflowId/edit'
            ]}
          >
            {workflow && (
              <WorkflowTabEdit
                {...tabProps}
                config={config}
                onChangeConfig={() => {
                  // unset config once editor is updated
                  setConfig(undefined);
                }}
              />
            )}
          </Route>
        </Switch>
      </PageTemplateDashboard>
      <AppSnackbar severity='error' open={!!error}>
        {error?.message ||
          'There was an error getting data from the server. Please check your internect connection.'}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => setDidUpdate(false)}
      >
        Workflow Saved
      </AppSnackbar>
    </StyledPageTemplate>
  );
};

export default WorkflowDetailPage;
